import { useMemo } from 'react';
import { type MessageDescriptor, defineMessages } from 'react-intl';

import { useAuthContext } from '@/contexts/auth-context';

import { useNavigationPaths } from './use-navigation-paths';
import { useSourceFreemium } from './use-source-freemium';

const messages = defineMessages({
  openings: {
    defaultMessage: 'Openings',
    description:
      'Left nav: sourcing home for an account without a Source subscription, whose dashboard is locked',
  },
  dashboard: {
    defaultMessage: 'Dashboard',
    description: 'Left nav: org sourcing home and metrics',
  },
  allOpenings: {
    defaultMessage: 'All Openings',
    description: 'Left nav: list of jobs to add to dashboard',
  },
  campaigns: {
    defaultMessage: 'Campaigns',
    description: 'Left nav: list of all sourcing campaigns across openings',
  },
  settings: {
    defaultMessage: 'Settings',
    description: 'Left nav: admin-only Source product settings',
  },
});

export type SourceNavItem = {
  key: keyof typeof messages;
  path: string;
  label: MessageDescriptor;
};

/**
 * The Source pages this user can open, in sidebar order. The fallback sidebar
 * and the universal search palette both list these, so neither offers a page
 * the routes turn the user away from: a free-tier account has no dashboard,
 * and Settings is admin-only.
 */
export function useSourceNavItems(): SourceNavItem[] {
  const isFreemium = useSourceFreemium();
  const { isAdmin } = useAuthContext();
  const {
    openingsPath,
    dashboardPath,
    allOpeningsPath,
    allCampaignsPath,
    sourceSettingsPath,
  } = useNavigationPaths();

  return useMemo(() => {
    const items: SourceNavItem[] = isFreemium
      ? [{ key: 'openings', path: openingsPath, label: messages.openings }]
      : [
          { key: 'dashboard', path: dashboardPath, label: messages.dashboard },
          {
            key: 'allOpenings',
            path: allOpeningsPath,
            label: messages.allOpenings,
          },
        ];
    items.push({
      key: 'campaigns',
      path: allCampaignsPath,
      label: messages.campaigns,
    });
    if (isAdmin) {
      items.push({
        key: 'settings',
        path: sourceSettingsPath,
        label: messages.settings,
      });
    }
    return items;
  }, [
    isFreemium,
    isAdmin,
    openingsPath,
    dashboardPath,
    allOpeningsPath,
    allCampaignsPath,
    sourceSettingsPath,
  ]);
}
